import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormDescription, FormMessage } from '@@/ui/primitives/form';
import { Input } from '@@/ui/primitives/input';
import { FormControl } from '@@/ui/primitives/form';
import { FormItem } from '@@/ui/primitives/form';
import { FormLabel } from '@@/ui/primitives/form';
import { FormField } from '@@/ui/primitives/form';
import { Button } from '@@/ui/primitives/button';
import { Form } from '@@/ui/primitives/form';
import { Checkbox } from '@@/ui/primitives/checkbox';
import { InfoPopover } from './InfoPopover';

const formSchema = z.object({
  name: z.string().min(1, {
    message: 'Name is required.'
  }),
  age: z.number().int().positive({
    message: 'Age must be a positive integer.'
  }),
  address: z.string().optional(),
  consent: z.boolean().optional()
});

export function FormExample() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      age: undefined,
      address: '',
      consent: false
    }
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
  }

  return (
    <div className='w-full max-w-2xl'>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <div className='grid grid-cols-2 gap-6'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder='Enter your name' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='age'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Age *</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      placeholder='Enter your age'
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='address'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Address <InfoPopover message='This is your address' />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder='Enter your address' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='consent'
              render={({ field }) => (
                <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-1 leading-none'>
                    <FormLabel>Consent to receive mail</FormLabel>
                    <FormDescription>You can unsubscribe at any time</FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
          <Button type='submit'>Submit</Button>
        </form>
      </Form>
    </div>
  );
}
